// src/modules/PackageManager/EditPackageMappingModal.tsx
import React, { useState, useEffect } from 'react';
import { X, Save, AlertCircle, Package2, Search, Check } from 'lucide-react';
import { PackageIdentifierMapping, searchPackageIdentifiers, fetchAgentsBySoftwareName } from '../../components/services/PackageManager';
import { useDebounce } from '../../hooks/useDebounce';
import { useDeviceManagementService } from '../../components/services/deviceManagementService';

interface EditPackageMappingModalProps {
  show: boolean;
  mapping: PackageIdentifierMapping | null;
  onClose: () => void;
  onSave: (packageIdentifier: string, isDefatomManaged: boolean, isVerified: boolean, notes: string) => Promise<void>;
  saving: boolean;
}

const EditPackageMappingModal: React.FC<EditPackageMappingModalProps> = ({ show, mapping, onClose, onSave, saving }) => {
  const { syncEndpoints } = useDeviceManagementService();

  const [packageIdentifier, setPackageIdentifier] = useState('');
  const [notes, setNotes] = useState('');
  const [isDefatomManaged, setIsDefatomManaged] = useState(false);
  const [error, setError] = useState('');

  // Track initial values to detect changes
  const [initialPackageIdentifier, setInitialPackageIdentifier] = useState('');
  const [initialIsDefatomManaged, setInitialIsDefatomManaged] = useState(false);
  const [initialIsVerified, setInitialIsVerified] = useState(false);

  // Search suggestions state
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(-1);

  // Debounce the search query
  const debouncedSearchQuery = useDebounce(packageIdentifier, 500);

  useEffect(() => {
    if (mapping) {
      const pkgId = mapping.packageIdentifier || '';
      const isDM = mapping.isDefatomManaged || false;
      const isVer = mapping.isVerified || false;

      setPackageIdentifier(pkgId);
      setInitialPackageIdentifier(pkgId);
      setNotes(mapping.notes || '');
      setIsDefatomManaged(isDM);
      setInitialIsDefatomManaged(isDM);
      setInitialIsVerified(isVer);
      setError('');
      setSuggestions([]);
      setShowSuggestions(false);
    }
  }, [mapping]);

  // Fetch package suggestions
  useEffect(() => {
    const fetchSuggestions = async () => {
      // Only search if query has at least 3 characters
      if (debouncedSearchQuery.trim().length < 3) {
        setSuggestions([]);
        setShowSuggestions(false);
        return;
      }

      try {
        setLoadingSuggestions(true);
        const result = await searchPackageIdentifiers(debouncedSearchQuery.trim(), 10);

        if (result.isSuccess && result.data) {
          setSuggestions(result.data.packageIdentifiers || []);
          setShowSuggestions(result.data.packageIdentifiers.length > 0);
        } else {
          setSuggestions([]);
          setShowSuggestions(false);
        }
      } catch (err) {
        console.error('Failed to fetch package suggestions:', err);
        setSuggestions([]);
        setShowSuggestions(false);
      } finally {
        setLoadingSuggestions(false);
      }
    };

    fetchSuggestions();
  }, [debouncedSearchQuery]);

  if (!show || !mapping) return null;

  const handleSave = async () => {
    const hasPackageId = packageIdentifier.trim().length > 0;

    // New validation logic
    if (hasPackageId) {
      // If we have a package identifier, validate it
      if (packageIdentifier.length < 3) {
        setError('Package Identifier must be at least 3 characters');
        return;
      }

      // Force isDefatomManaged to true when package identifier exists
      if (!isDefatomManaged) {
        setError('Packages with identifier must be Defatom Managed. Uncheck "Mark as UnManaged" or remove the package identifier.');
        return;
      }
    }

    // If no package identifier, isDefatomManaged should be false
    const finalIsDefatomManaged = hasPackageId ? isDefatomManaged : false;

    setError('');
    const finalPackageId = packageIdentifier.trim();

    // Check if any changes were made and if isVerified was initially true
    const hasChanges = packageIdentifier !== initialPackageIdentifier || isDefatomManaged !== initialIsDefatomManaged;

    if (hasChanges && initialIsVerified && mapping?.registryDisplayName) {
      try {
        console.log('Fetching agents for software:', mapping.registryDisplayName);
        const response = await fetchAgentsBySoftwareName(mapping.registryDisplayName);

        if (response.isSuccess && response.data) {
          // Get all agent IDs across all tenants for logging
          const allAgentIds = response.data.tenants.flatMap((tenant) => tenant.agents.map((agent) => agent.agentId));

          // Sync endpoints per tenant using the service
          if (allAgentIds.length > 0) {
            for (const tenant of response.data.tenants) {
              // ← Loops through each tenant
              if (tenant.agents.length > 0) {
                const tenantAgentIds = tenant.agents.map((agent) => agent.agentId); // ← Gets agent IDs for this tenant
                console.log(`Syncing ${tenantAgentIds.length} agents for tenant: ${tenant.tenantId}`);

                try {
                  await syncEndpoints(tenant.tenantId, tenantAgentIds); // ← Calls sync with tenantId + agentIdList
                  console.log(`✓ Successfully synced agents for tenant: ${tenant.tenantId}`);
                } catch (error) {
                  console.error(`✗ Failed to sync agents for tenant: ${tenant.tenantId}`, error);
                }
              }
            }
          }
        } else {
          console.log('No agents found or failed to fetch agents:', response.message);
        }
      } catch (error) {
        console.error('Error fetching agents:', error);
      }
    }

    // Always pass true for isVerified
    await onSave(finalPackageId, finalIsDefatomManaged, true, notes.trim());
  };

  const handleSelectSuggestion = (suggestion: string) => {
    setPackageIdentifier(suggestion);
    setShowSuggestions(false);
    setSuggestions([]);
    setSelectedSuggestionIndex(-1);
    setError('');

    // Auto-set isDefatomManaged to true only when selecting a non-empty package
    if (suggestion.trim().length > 0) {
      setIsDefatomManaged(true);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    // Handle arrow keys for suggestion navigation
    if (showSuggestions && suggestions.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedSuggestionIndex((prev) => (prev < suggestions.length - 1 ? prev + 1 : prev));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedSuggestionIndex((prev) => (prev > 0 ? prev - 1 : -1));
      } else if (e.key === 'Enter' && selectedSuggestionIndex >= 0) {
        e.preventDefault();
        handleSelectSuggestion(suggestions[selectedSuggestionIndex]);
        return;
      }
    }

    if (e.key === 'Enter' && !e.shiftKey && !showSuggestions) {
      e.preventDefault();
      handleSave();
    } else if (e.key === 'Escape') {
      if (showSuggestions) {
        setShowSuggestions(false);
        setSelectedSuggestionIndex(-1);
      } else {
        onClose();
      }
    }
  };

  const getCheckboxStyle = (checked: boolean) => {
    return {
      width: '20px',
      height: '20px',
      borderRadius: '4px',
      border: checked ? '2px solid #00FF00' : '2px solid #444',
      backgroundColor: checked ? '#00FF00' : 'transparent',
      cursor: 'pointer',
      display: 'inline-block',
    };
  };

  // Check if no changes were made and packageIdentifier was initially null
  const hasNoChanges = packageIdentifier === initialPackageIdentifier && isDefatomManaged === initialIsDefatomManaged;

  const wasPackageIdentifierNull = !initialPackageIdentifier || initialPackageIdentifier.trim() === '';

  const shouldShowVerifyButton = hasNoChanges && wasPackageIdentifierNull;
  const buttonText = shouldShowVerifyButton ? 'Save Changes' : 'Save Changes';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm transition-opacity animate-fade-in">
      <div className="bg-gradient-to-br from-[#1c1c1c] to-[#2a2a2a] text-white rounded-2xl shadow-2xl p-6 w-[600px] max-h-[85vh] overflow-hidden transform transition-all duration-300 scale-100 animate-slide-up border border-gray-700">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-700 pb-4 mb-5">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-900/20 rounded-lg">
              <Package2 className="text-red-500" size={24} />
            </div>
            <div>
              <h2 className="text-xl font-bold">Edit Package Mapping</h2>
              <p className="text-xs text-gray-400 mt-0.5">{mapping.registryDisplayName}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={saving}
            className="p-2 rounded-full hover:bg-red-900/30 transition-colors disabled:opacity-50"
          >
            <X className="text-gray-400 hover:text-white" size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="space-y-5 overflow-y-auto max-h-[calc(85vh-180px)] scrollbar-thin scrollbar-thumb-gray-600 scrollbar-track-gray-800 pr-2">
          {/* Package Identifier Input with Search */}
          <div className="relative">
            <label className="block text-sm font-semibold text-gray-300 mb-2">
              Package Identifier <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <input
                type="text"
                value={packageIdentifier}
                onChange={(e) => {
                  const newValue = e.target.value;
                  setPackageIdentifier(newValue);
                  setError('');
                  setSelectedSuggestionIndex(-1);

                  // Auto-set isDefatomManaged to true only if package identifier has content
                  if (newValue.trim().length > 0) {
                    setIsDefatomManaged(true);
                  }
                }}
                onKeyDown={handleKeyPress}
                onFocus={() => {
                  if (suggestions.length > 0) {
                    setShowSuggestions(true);
                  }
                }}
                placeholder="e.g., Google.Chrome, Mozilla.Firefox"
                disabled={saving}
                className="w-full px-4 py-3 pr-10 bg-[#2a2a2a] text-white rounded-lg border border-gray-600 focus:outline-none focus:border-red-500 focus:ring-2 focus:ring-red-500/20 transition-all disabled:opacity-50 disabled:cursor-not-allowed font-mono text-sm"
              />
              {loadingSuggestions && (
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                  <div className="w-5 h-5 border-2 border-red-500 border-t-transparent rounded-full animate-spin" />
                </div>
              )}
              {!loadingSuggestions && packageIdentifier.length >= 3 && (
                <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500" size={18} />
              )}
            </div>

            {/* Suggestions Dropdown */}
            {showSuggestions && suggestions.length > 0 && (
              <div className="absolute z-10 w-full mt-1 bg-[#2a2a2a] border border-gray-600 rounded-lg shadow-xl max-h-60 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-600 scrollbar-track-gray-800">
                {suggestions.map((suggestion, index) => (
                  <button
                    key={index}
                    type="button"
                    onClick={() => handleSelectSuggestion(suggestion)}
                    className={`w-full px-4 py-3 text-left font-mono text-sm hover:bg-red-900/20 transition-colors flex items-center justify-between ${
                      index === selectedSuggestionIndex ? 'bg-red-900/30' : ''
                    } ${index === 0 ? 'rounded-t-lg' : ''} ${index === suggestions.length - 1 ? 'rounded-b-lg' : ''}`}
                  >
                    <span className="text-blue-400">{suggestion}</span>
                    {packageIdentifier.toLowerCase() === suggestion.toLowerCase() && <Check className="text-green-500" size={16} />}
                  </button>
                ))}
              </div>
            )}

            {error && (
              <div className="flex items-center gap-2 mt-2 text-red-400 text-sm">
                <AlertCircle size={16} />
                <span>{error}</span>
              </div>
            )}
            <p className="text-xs text-gray-500 mt-1.5">
              {/* Type at least 3 characters to search. Use standard package naming convention (e.g., Vendor.ProductName) */}
            </p>
          </div>

          {/* Current Info Display */}
          <div className="bg-[#252525] rounded-lg p-4 border border-gray-700">
            <h3 className="text-sm font-semibold text-gray-300 mb-3">Current Information</h3>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <span className="text-gray-500">Display Name:</span>
                <p className="text-white font-medium mt-0.5">{mapping.registryDisplayName}</p>
              </div>
              <div>
                <span className="text-gray-500">Publisher:</span>
                <p className="text-white font-medium mt-0.5">{mapping.registryPublisher || '-'}</p>
              </div>
              <div>
                <span className="text-gray-500">Product Code:</span>
                <p className="text-white font-mono text-xs mt-0.5">{mapping.productCode || '-'}</p>
              </div>
              <div>
                <span className="text-gray-500">Priority:</span>
                <p className="text-white font-medium mt-0.5">{mapping.priority >= 0 ? mapping.priority : 'Auto-created'}</p>
              </div>
            </div>
          </div>

          {/* Is Defatom Managed Checkbox */}
          <div className="flex items-center gap-3">
            <div
              onClick={() => {
                if (!saving) {
                  const hasPackageId = packageIdentifier.trim().length > 0;
                  const newValue = !isDefatomManaged;

                  // If package identifier exists and user tries to mark as unmanaged, show error
                  if (hasPackageId && !newValue) {
                    setError('Cannot mark as UnManaged when Package Identifier exists. Remove the package identifier first.');
                    return;
                  }

                  setIsDefatomManaged(newValue);

                  // Clear error when changing checkbox
                  setError('');
                }
              }}
              style={getCheckboxStyle(!isDefatomManaged)}
            />
            <label
              onClick={() => {
                if (!saving) {
                  const hasPackageId = packageIdentifier.trim().length > 0;
                  const newValue = !isDefatomManaged;

                  // If package identifier exists and user tries to mark as unmanaged, show error
                  if (hasPackageId && !newValue) {
                    setError('Cannot mark as UnManaged when Package Identifier exists. Remove the package identifier first.');
                    return;
                  }

                  setIsDefatomManaged(newValue);

                  // Clear error when changing checkbox
                  setError('');
                }
              }}
              className="text-sm font-semibold text-gray-300 cursor-pointer"
            >
              Mark as UnManaged
            </label>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-semibold text-gray-300 mb-2">Notes (Optional)</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add any additional notes or comments..."
              disabled={saving}
              rows={4}
              className="w-full px-4 py-3 bg-[#2a2a2a] text-white rounded-lg border border-gray-600 focus:outline-none focus:border-red-500 focus:ring-2 focus:ring-red-500/20 transition-all disabled:opacity-50 disabled:cursor-not-allowed text-sm resize-none scrollbar-thin scrollbar-thumb-gray-600 scrollbar-track-gray-800"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 mt-6 pt-5 border-t border-gray-700">
          <button
            onClick={onClose}
            disabled={saving}
            className="px-5 py-2.5 border border-gray-600 rounded-lg text-gray-300 hover:bg-gray-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed font-medium"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-5 py-2.5 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 rounded-lg text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 shadow-lg shadow-red-500/20 font-medium"
          >
            {saving ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                {shouldShowVerifyButton ? 'Saving...' : 'Saving...'}
              </>
            ) : (
              <>
                <Save size={18} />
                {buttonText}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditPackageMappingModal;
